import React, { useState } from 'react';
import { 
  Paper, 
  Typography, 
  Link, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Chip,
  Box,
  TablePagination
} from '@mui/material';
import { format, isAfter, differenceInDays } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { UtilityService } from '../../../types/utility.types';

interface ContractRenewalTimelineProps {
  services: UtilityService[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  onPageChange?: (page: number, limit: number) => void;
}

const ContractRenewalTimeline: React.FC<ContractRenewalTimelineProps> = ({ 
  services = [], 
  pagination,
  onPageChange 
}) => {
  const navigate = useNavigate();
  const [rowsPerPage, setRowsPerPage] = useState(pagination?.limit || 10);
  
  // Debug logging to check renewalStatus
  console.log('ContractRenewalTimeline - Total services:', services.length);
  if (services.length > 0) {
    console.log('First service:', services[0]);
    console.log('First service renewalStatus:', services[0].renewalStatus);
    console.log('All services renewalStatus:', services.map(s => ({ id: s._id, renewalStatus: s.renewalStatus })));
  }
  
  const renewals = services
    .filter(service => service.contractEndDate)
    .filter(service => !service.renewalStatus || service.renewalStatus === 'NO_RENEWAL') // Show contracts needing action (including legacy contracts without renewalStatus)
    .map(service => {
      const endDate = service.contractEndDate ? new Date(service.contractEndDate) : null;
      const isOverdue = endDate ? isAfter(new Date(), endDate) : false;
      const daysUntilExpiry = endDate ? differenceInDays(endDate, new Date()) : null;
      
      return {
        ...service,
        endDate,
        isOverdue,
        daysUntilExpiry
      };
    })
    .filter(service => {
      // Include contracts that are:
      // 1. Not yet expired (daysUntilExpiry >= 0)
      // 2. Overdue but within 60 days past expiry (daysUntilExpiry >= -60)
      return service.daysUntilExpiry !== null && service.daysUntilExpiry >= -60;
    })
    .sort((a, b) => {
      if (!a.endDate || !b.endDate) return 0;
      return a.endDate.getTime() - b.endDate.getTime();
    });

  const handleClientClick = (clientId: string) => {
    navigate(`/utilities/clients/${clientId}`);
  };

  const getUrgencyColor = (daysUntilExpiry: number | null, isOverdue: boolean) => {
    if (isOverdue) return 'error';
    if (daysUntilExpiry === null) return 'default';
    if (daysUntilExpiry <= 30) return 'error';
    if (daysUntilExpiry <= 60) return 'warning';
    return 'success';
  };

  const getUrgencyLabel = (daysUntilExpiry: number | null, isOverdue: boolean) => {
    if (isOverdue) return 'OVERDUE';
    if (daysUntilExpiry === null) return 'Unknown';
    if (daysUntilExpiry <= 30) return 'CRITICAL';
    if (daysUntilExpiry <= 60) return 'WARNING';
    return 'OK';
  };

  if (renewals.length === 0) {
    return (
      <Paper sx={{ 
        p: 2,
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(4px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: 2,
        color: 'white'
      }}>
        <Typography sx={{ color: 'white' }}>No upcoming contract renewals</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ 
      p: 2,
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      backdropFilter: 'blur(4px)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: 2,
      color: 'white'
    }}>
      <Typography variant="h6" gutterBottom sx={{ color: '#fff', mb: 2 }}>
        Contract Renewals
      </Typography>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Renewal</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Client</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Service</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Supplier</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Expires</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Days Left</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {renewals.map((service) => (
              <TableRow key={service._id} hover>
                <TableCell>
                  {service.renewalStatus === 'PENDING_RENEWAL' && (
                    <Chip
                      label="Renewal Pending"
                      color="info"
                      size="small"
                      sx={{ fontWeight: 'bold' }}
                    />
                  )}
                  {service.renewalStatus === 'RENEWED' && (
                    <Chip
                      label="Renewed"
                      color="success"
                      size="small"
                      sx={{ fontWeight: 'bold' }}
                    />
                  )}
                  {service.renewalStatus === 'NO_RENEWAL' && (
                    <Chip
                      label="Action Needed"
                      color="warning"
                      size="small"
                      sx={{ fontWeight: 'bold' }}
                    />
                  )}
                </TableCell>
                <TableCell>
                  {service.client && ((service.client as any).companyName || (service.client as any).name) ? (
                    <Link
                      component="button"
                      variant="inherit"
                      onClick={() => handleClientClick((service.client as any)._id || service._id)}
                      sx={{ 
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        fontWeight: 'bold',
                        color: '#fff',
                        fontSize: '0.875rem',
                        '&:hover': {
                          textDecoration: 'none',
                          color: '#e0e0e0'
                        }
                      }}
                    >
                      {(service.client as any).companyName || (service.client as any).name}
                    </Link>
                  ) : (
                    <Typography sx={{ color: '#fff', fontSize: '0.875rem' }}>Unknown Client</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography sx={{ color: '#fff', fontSize: '0.875rem', fontWeight: 'bold' }}>
                    {service.serviceType}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography sx={{ color: '#fff', fontSize: '0.875rem' }}>
                    {typeof service.supplier === 'object' && service.supplier !== null && 'name' in service.supplier
                      ? (service.supplier as any).name
                      : typeof service.supplier === 'string'
                      ? service.supplier
                      : 'Unknown Supplier'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography sx={{ color: '#fff', fontSize: '0.875rem' }}>
                    {service.endDate ? format(service.endDate, 'MMM dd, yyyy') : 'No end date'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography 
                    sx={{ 
                      color: service.isOverdue ? '#f44336' : '#fff', 
                      fontSize: '0.875rem',
                      fontWeight: service.daysUntilExpiry !== null && Math.abs(service.daysUntilExpiry) <= 30 ? 'bold' : 'normal'
                    }}
                  >
                    {service.daysUntilExpiry !== null 
                      ? `${service.daysUntilExpiry} days`
                      : 'Unknown'
                    }
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      {pagination && onPageChange && (
        <Box sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', mt: 1 }}>
          <TablePagination
            component="div"
            count={pagination.total}
            page={pagination.page - 1} // MUI uses 0-indexed pages
            onPageChange={(_, newPage) => onPageChange(newPage + 1, rowsPerPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              const newLimit = parseInt(event.target.value, 10);
              setRowsPerPage(newLimit);
              onPageChange(1, newLimit); // Reset to page 1 when changing limit
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              color: '#fff',
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                color: '#fff'
              },
              '& .MuiTablePagination-select': {
                color: '#fff'
              },
              '& .MuiTablePagination-actions button': {
                color: '#fff'
              },
              '& .MuiSelect-icon': {
                color: '#fff'
              }
            }}
          />
        </Box>
      )}
    </Paper>
  );
};

export { ContractRenewalTimeline };
